'use client';


/** Imports */
import classNames from 'classnames';
import styles from './dialog.module.css';
import {useTranslations} from 'next-intl';
import {MdOutlineClose} from 'react-icons/md';
import Confetti from '@/components/Confetti/Confetti';
import React, {useEffect, useId, useState, useRef} from 'react';


/** Types */
type Props = Readonly<{
    reference: React.RefObject<HTMLDialogElement | null>;
    headline: string | React.ReactNode;
    subHeadline?: string | React.ReactNode;
    children?: React.ReactNode;
    disableCloseOnBackdrop?: boolean;
    disableManualScrolling?: boolean;
    foot?: React.ReactNode;
    extraClasses?: Array<string>;
    noCloseButton?: boolean;
    closeButtonLabel?: string;
    startOpen?: boolean;
    confetti?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
}>;


/** Component */
export default function Dialog(props: Props) {
    const {
        reference, headline, subHeadline, children, disableCloseOnBackdrop, disableManualScrolling,
        foot, extraClasses, noCloseButton, closeButtonLabel, startOpen, confetti, onClose, onOpen,
    } = props;

    // Hooks
    const tAria = useTranslations('common.ariaLabels');


    // Refs
    const confettiRef = useRef<HTMLDialogElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headlineId = useId();
    const subHeadlineId = useId();

    // State
    const [isSticky, setIsSticky] = useState(false);

    // Effects
    useEffect(() => {
        if (startOpen) reference.current?.showModal();
    }, [startOpen, reference]);
    useEffect(() => {
        if (!scrollRef.current) return;
        const abortController = new AbortController();
        if (disableManualScrolling) {
            scrollRef.current.addEventListener('touchmove', preventDefaultHandler, {
                passive: false,
                signal: abortController.signal,
            });
            scrollRef.current.addEventListener('wheel', preventDefaultHandler, {
                passive: false,
                signal: abortController.signal,
            });
        } else {
            scrollRef.current.removeEventListener('touchmove', preventDefaultHandler);
            scrollRef.current.removeEventListener('wheel', preventDefaultHandler);
        }

        function preventDefaultHandler(e: Event) {
            e.preventDefault();
        }

        // Cleanup
        return () => abortController.abort();
    }, [disableManualScrolling]);


    // Event handlers
    function handleScroll(event: React.UIEvent<HTMLDivElement>) {
        const scrollTop = event.currentTarget.scrollTop;
        setIsSticky(scrollTop > 0);
    }

    function handleToggle(event: React.ToggleEvent<HTMLDialogElement>) {
        if (event.currentTarget.open) {
            onOpen?.();
            confettiRef.current?.show();
            return;
        }
        onClose?.();
        confettiRef.current?.close();
    }


    // Render
    return (<>

        {/* Dialog */}
        <dialog
            ref={reference}
            className={classNames(styles.dialog, ...(extraClasses || []))}
            aria-labelledby={headlineId}
            aria-describedby={
                (subHeadline)
                    ? subHeadlineId
                    : undefined
            }
            onClick={(event) => !disableCloseOnBackdrop
                && (event.target === reference.current)
                && reference.current?.close()}
            onToggle={handleToggle}
            onCancel={(event) => noCloseButton && event.preventDefault()}
        >
            <div
                ref={scrollRef}
                className={classNames(
                    styles.scrollWrapper,
                    'dialog-scroll-wrapper',
                    {'custom-scrollbar': !disableManualScrolling},
                    {'no-scrollbar': disableManualScrolling},
                )}
                onScroll={handleScroll}
            >
                <div className={classNames(styles.head, 'dialog-head', {[styles.sticky]: isSticky})}>
                    <div id={headlineId} className={styles.headline}>
                        {headline}
                    </div>
                    {subHeadline && (
                        <div id={subHeadlineId} className={styles.subHeadline}>
                            {subHeadline}
                        </div>
                    )}
                    {!noCloseButton && (
                        <button
                            type="button"
                            className={classNames(styles.closeButton, 'dialog-close')}
                            onClick={() => reference.current?.close()}
                            aria-label={closeButtonLabel ?? tAria('closeDialog')}
                        >
                            <MdOutlineClose aria-hidden="true"/>
                        </button>
                    )}
                </div>
                <div className={classNames(styles.body, 'dialog-body')}>
                    {children}
                </div>
            </div>
            {foot && (
                <div className={classNames(styles.foot, 'dialog-foot')}>
                    {foot}
                </div>
            )}
        </dialog>

        {/* Confetti */}
        {confetti && (
            <dialog ref={confettiRef} className={styles.confettiDialog}>
                <Confetti/>
            </dialog>
        )}
    </>);
}
